// src/components/learning/LetterLibrary.js
import React, { useState, useEffect, useCallback } from 'react';
import ArabicLetterCard from './ArabicLetterCard';
import { arabicLetters } from './arabicLettersData';

  const LetterLibrary = ({ onClose, client, channelName, userType, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [viewMode, setViewMode] = useState('card'); // 'card' ou 'grid'
  const [isSharing, setIsSharing] = useState(false);

  // 📤 Envoyer la lettre actuelle à l'élève
  const sendLetterToStudent = useCallback(async (index) => {
    if (!client || userType !== 'prof') return;
    
    try {
      const message = {
        type: 'alphabet_sync',
        letterIndex: index,
        letter: arabicLetters[index],
        timestamp: Date.now()
      };
      
      const encoder = new TextEncoder();
      await client.sendStreamMessage({
        payload: encoder.encode(JSON.stringify(message))
      });
      
      console.log('📤 Lettre envoyée:', arabicLetters[index].name);
    } catch (error) {
      console.error('❌ Erreur envoi lettre:', error);
    }
  }, [client, userType]);

  // 📥 Écouter les messages de synchronisation
  useEffect(() => {
    if (!client) return;

    const handleStreamMessage = (uid, payload) => {
      try {
        const decoder = new TextDecoder();
        const message = JSON.parse(decoder.decode(payload));
        
        if (message.type === 'alphabet_sync') {
          console.log('📥 Lettre reçue:', message.letter.name);
          setCurrentIndex(message.letterIndex);
          setViewMode('card');
          setIsSharing(true);
        } else if (message.type === 'alphabet_close') {
          console.log('📥 Fermeture alphabet reçue');
          onClose();
        }
      } catch (error) {
        console.error('Erreur parsing message:', error);
      }
    };

    client.on('stream-message', handleStreamMessage);
    
    return () => {
      client.off('stream-message', handleStreamMessage);
    };
  }, [client, onClose]);

  // 🔄 Navigation avec partage automatique
  const handleNext = () => {
    if (currentIndex < arabicLetters.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      if (isSharing && userType === 'prof') {
        sendLetterToStudent(newIndex);
      }
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      if (isSharing && userType === 'prof') {
        sendLetterToStudent(newIndex);
      }
    }
  };

  const handleSelectLetter = (index) => {
    setCurrentIndex(index);
    setViewMode('card');
    if (isSharing && userType === 'prof') {
      sendLetterToStudent(index);
    }
  };

  // 🚀 Démarrer le partage
  const startSharing = () => {
    setIsSharing(true);
    sendLetterToStudent(currentIndex);
  };

  // ⏹️ Arrêter le partage
  const stopSharing = async () => {
    setIsSharing(false);
    if (client && userType === 'prof') {
      try {
        const message = { type: 'alphabet_close' };
        const encoder = new TextEncoder();
        await client.sendStreamMessage({
          payload: encoder.encode(JSON.stringify(message))
        });
      } catch (error) {
        console.error('Erreur fermeture:', error);
      }
    }
  };

  // 🚪 Fermer avec notification
  const handleClose = () => {
    if (isSharing && userType === 'prof') {
      stopSharing();
    }
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '1rem 1.5rem',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '1.8rem' }}>🔤</span>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.3rem' }}>
              Alphabet Arabe
            </h2>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
              {arabicLetters.length} lettres • Cliquez pour écouter
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {/* Bouton de partage (prof uniquement) */}
          {userType === 'prof' && client && (
            <button
              onClick={isSharing ? stopSharing : startSharing}
              style={{
                padding: '0.6rem 1.2rem',
                borderRadius: '8px',
                border: 'none',
                background: isSharing 
                  ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {isSharing ? '⏹️ Arrêter le partage' : '🚀 Partager avec l\'élève'}
            </button>
          )}

          {/* Indicateur de partage (élève) */}
          {userType !== 'prof' && isSharing && (
            <div style={{
              padding: '0.5rem 1rem',
              background: 'rgba(16, 185, 129, 0.2)',
              borderRadius: '8px',
              color: '#10b981',
              fontSize: '0.85rem'
            }}>
              🔴 En direct avec le prof
            </div>
          )}

          {/* Toggle vue */}
          <button
            onClick={() => setViewMode(viewMode === 'card' ? 'grid' : 'card')}
            style={{
              padding: '0.6rem 1rem',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'transparent',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            {viewMode === 'card' ? '📊 Grille' : '🎴 Fiche'}
          </button>

          {/* Fermer */}
          <button
            onClick={handleClose}
            style={{
              padding: '0.6rem 1rem',
              borderRadius: '8px',
              border: 'none',
              background: 'rgba(255,255,255,0.1)',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1.2rem'
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Contenu */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {viewMode === 'card' ? (
          <ArabicLetterCard
            letter={arabicLetters[currentIndex]}
            onNext={handleNext}
            onPrevious={handlePrevious}
            currentIndex={currentIndex}
            totalLetters={arabicLetters.length}
          />
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
            gap: '1rem',
            width: '100%',
            maxWidth: '800px'
          }}>
            {arabicLetters.map((letter, index) => (
              <button
                key={letter.id}
                onClick={() => handleSelectLetter(index)}
                style={{
                  padding: '1.5rem 1rem',
                  borderRadius: '12px',
                  border: currentIndex === index ? '3px solid #667eea' : '2px solid rgba(255,255,255,0.1)',
                  background: currentIndex === index 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'rgba(255,255,255,0.05)',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ 
                  fontSize: '2.5rem', 
                  fontFamily: "'Amiri', 'Traditional Arabic', serif",
                  marginBottom: '0.5rem'
                }}>
                  {letter.letter}
                </div>
                <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                  {letter.name}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer avec progression */}
      <div style={{
        padding: '1rem 1.5rem',
        background: 'rgba(0,0,0,0.3)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        {arabicLetters.map((_, index) => (
          <div
            key={index}
            onClick={() => handleSelectLetter(index)}
            style={{
              width: index === currentIndex ? '24px' : '8px',
              height: '8px',
              borderRadius: '4px',
              background: index === currentIndex ? '#667eea' : 'rgba(255,255,255,0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default LetterLibrary;
